import { useMemo, useState } from 'react';
import { useGetScreenGridsInfo } from 'src/features/locations/scheduleComponentPage/hooks/useGetScreenGridsInfo';
import {
  getCurrentPositionGridLines,
  getLayerScreensDisclaimerInfo,
} from 'src/features/locations/scheduleComponentPage/ScheduleComponentPage.helper';
import {
  IDefaultSection,
  IGridSection,
  IPositionGridLines,
  TScreensGridSections,
} from 'src/features/locations/scheduleComponentPage/ScheduleComponentPage.interface';
import { ILayout } from 'src/interfaces';
import { calculateOffset, lang } from 'src/utils';

export const useManagePositionLayout = (
  screensGridSections: TScreensGridSections,
  layerIndex: number,
  screenIndex: number,
  previewingPositionIndex: number,
  layout: ILayout | undefined,
  positionsGridLines: IPositionGridLines[],
  setScreensGridSections: React.Dispatch<
    React.SetStateAction<TScreensGridSections>
  >,
  setShowSectionDetailDrawer: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const [showLayoutScreenBuilder, setShowLayoutScreenBuilder] = useState(false);
  const [currentSelectedSectionId, setCurrentSelectedSectionId] = useState<
    string | null
  >(null);
  const screenSections: (IGridSection | IDefaultSection)[] = useMemo(
    () => screensGridSections?.[layerIndex]?.[screenIndex] ?? [],
    [layerIndex, screenIndex, screensGridSections],
  );

  const gridSectionsWithoutDisclaimer = useMemo(
    () => screenSections.filter((section) => !section.isDisclaimer),
    [screenSections],
  ) as IGridSection[];

  const layerScreenSections: Record<
    number,
    (IGridSection | IDefaultSection)[]
  > =
    screensGridSections?.[
      layerIndex === -1 ? previewingPositionIndex : layerIndex
    ] ?? {};

  const currentPositionGridLines = getCurrentPositionGridLines(
    positionsGridLines,
    layout,
    layerIndex,
    previewingPositionIndex,
  );

  const {
    rows,
    cols,
    gridHeight,
    screenWidth,
    extraHeightForDisclaimerTopAdjustment,
  } = useGetScreenGridsInfo(currentPositionGridLines, screenIndex);

  const onActiveSectionChange = (newSectionId: string | null) => {
    setCurrentSelectedSectionId(newSectionId);
  };

  const onGridSectionsChange = (sections: IGridSection[]) => {
    setScreensGridSections((prev) => ({
      ...prev,
      [layerIndex]: {
        ...prev[layerIndex],
        [screenIndex]: [
          ...screenSections.filter((section) => section.isDisclaimer),
          ...sections.map((section, index) => {
            if (section.isDYR) return section;

            return {
              ...section,
              screenId: layout?.screens[screenIndex].id,
              name: `${lang.section} ${index + 1}`,
              height: Math.round((section.h * gridHeight!) / rows!),
              width: Math.round((section.w * screenWidth!) / cols!),
              topOffset: calculateOffset(
                (section.y * gridHeight!) / rows!,
                extraHeightForDisclaimerTopAdjustment!,
              ),
              leftOffset: Math.round((section.x * screenWidth!) / cols!),
              config: {},
              isDisclaimer: false,
              isDYR: false,
              canBeDeleted: true,
            };
          }),
        ],
      },
    }));
  };

  const onDeleteScreenSection = (screenId: string) => {
    onGridSectionsChange(
      gridSectionsWithoutDisclaimer.filter((item) => item.i !== screenId),
    );
  };

  const onUpdateScreenPredefinedSections = (screenConfigString: string) => {
    setShowSectionDetailDrawer(false);
    setScreensGridSections((prev) => {
      const disclaimerInfo = getLayerScreensDisclaimerInfo(
        [screenConfigString],
        [layout?.screens[screenIndex].id!],
      )[0];

      return {
        ...prev,
        [layerIndex]: {
          ...prev[layerIndex],
          [screenIndex]: [...disclaimerInfo],
        },
      };
    });
  };

  return {
    showLayoutScreenBuilder,
    gridSectionsWithoutDisclaimer,
    currentSelectedSectionId,
    screenSections,
    layerScreenSections,
    setShowLayoutScreenBuilder,
    onActiveSectionChange,
    onGridSectionsChange,
    onDeleteScreenSection,
    onUpdateScreenPredefinedSections,
  };
};
